// src/services/fcmTokenService.js
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { resetPushStateIfProjectChanged } from './pushStateReset';

// Firebase Cloud Messaging - lazily initialized
let messaging = null;
let messagingInitialized = false;
let messagingSupported = null;

// Your Firebase Web Push certificate key (get from Firebase Console > Project Settings > Cloud Messaging)
const VAPID_KEY = process.env.REACT_APP_VAPID_KEY;

// Check if the browser supports the required APIs for FCM
const checkBrowserSupport = () => {
  if (typeof window === 'undefined') return false;
  if (!('Notification' in window)) return false;
  if (!('serviceWorker' in navigator)) return false;
  if (!('PushManager' in window)) return false;
  return true;
};

// Check if Firebase Messaging is supported (async check)
const checkMessagingSupport = async () => {
  if (messagingSupported !== null) {
    return messagingSupported;
  }

  if (!checkBrowserSupport()) {
    messagingSupported = false;
    return false;
  }

  try {
    const { isSupported } = await import('firebase/messaging');
    messagingSupported = await isSupported();
    return messagingSupported;
  } catch (error) {
    console.log('Firebase messaging support check failed:', error.message);
    messagingSupported = false;
    return false;
  }
};

// Lazy initialization of messaging - only called when needed
const getMessagingInstance = async () => {
  if (messagingInitialized) {
    return messaging;
  }

  messagingInitialized = true;

  const supported = await checkMessagingSupport();
  if (!supported) {
    console.log('Firebase messaging not supported in this browser');
    return null;
  }

  try {
    const { getMessaging } = await import('firebase/messaging');
    messaging = getMessaging();
    return messaging;
  } catch (error) {
    console.log('Firebase messaging initialization failed:', error.message);
    return null;
  }
};

// Request notification permission and get FCM token
export const requestNotificationPermission = async () => {
  try {
    // Check support first
    const supported = await checkMessagingSupport();
    if (!supported) {
      console.log('Push notifications not supported in this browser');
      return null;
    }

    const messagingInstance = await getMessagingInstance();
    if (!messagingInstance) {
      console.log('Firebase messaging not available');
      return null;
    }

    // Request permission
    const permission = await Notification.requestPermission();

    if (permission === 'granted') {
      console.log('Notification permission granted.');

      try {
        const { getToken } = await import('firebase/messaging');

        // One time per browser and Firebase project: clear push state left over from a different project
        // (e.g. from before dev/staging/production were separated) so the token we get below is a valid one.
        await resetPushStateIfProjectChanged(process.env.REACT_APP_FIREBASE_PROJECT_ID);

        // Explicitly register the SW and wait for it to become active.
        // We do NOT call deleteToken or unsubscribe — those unregister the
        // token on FCM's servers and calling them on every page load was the
        // root cause of the persistent registration-token-not-registered errors.
        let swRegistration = null;
        try {
          // A service worker cannot read this app's environment variables, so pass THIS environment's
          // Firebase settings to it in the address. That way dev, staging and production each push
          // through their own Firebase project (nothing is hard-coded in the worker file).
          const swParams = new URLSearchParams({
            apiKey: process.env.REACT_APP_FIREBASE_API_KEY || '',
            authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || '',
            projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || '',
            storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || '',
            messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || '',
            appId: process.env.REACT_APP_FIREBASE_APP_ID || ''
          });
          swRegistration = await navigator.serviceWorker.register(`/firebase-messaging-sw.js?${swParams.toString()}`);
          if (!swRegistration.active) {
            await Promise.race([
              new Promise(resolve => {
                const poll = setInterval(() => {
                  if (swRegistration.active) { clearInterval(poll); resolve(); }
                }, 100);
              }),
              new Promise((_, reject) =>
                setTimeout(() => reject(new Error('SW activation timeout')), 10000)
              )
            ]);
          }
        } catch (swErr) {
          console.log('Service worker setup failed:', swErr.message);
          swRegistration = null;
        }

        if (!swRegistration?.active) {
          console.log('No active service worker — reload the page and try again.');
          return null;
        }

        const currentToken = await getToken(messagingInstance, {
          vapidKey: VAPID_KEY,
          serviceWorkerRegistration: swRegistration
        });

        if (currentToken) {
          console.log('FCM Token obtained successfully, starts with:', currentToken.substring(0, 10));
          return currentToken;
        } else {
          console.log('No registration token available.');
          return null;
        }
      } catch (tokenError) {
        console.log('Could not get FCM token:', tokenError.message);
        return null;
      }
    } else {
      console.log('Notification permission denied.');
      return null;
    }
  } catch (error) {
    console.log('Error in notification permission flow:', error.message);
    return null;
  }
};

// Save FCM token to user profile (for salon admin users)
export const saveAdminFCMToken = async (userId, token) => {
  try {
    if (!token) return false;

    const userDocRef = doc(db, 'salonUsers', userId);
    await updateDoc(userDocRef, {
      fcmToken: token,
      fcmTokenUpdated: new Date(),
      notificationSettings: {
        pushNotifications: true,
        emailNotifications: true
      }
    });

    console.log('Admin FCM token saved successfully');
    return true;
  } catch (error) {
    console.error('Error saving admin FCM token:', error);
    return false;
  }
};

// Save FCM token to customer profile
export const saveCustomerFCMToken = async (customerId, token) => {
  try {
    if (!token) return false;

    const customerDocRef = doc(db, 'customers', customerId);

    // Check if customer exists first
    const customerDoc = await getDoc(customerDocRef);
    if (!customerDoc.exists()) {
      console.error('Customer document not found:', customerId);
      return false;
    }

    await updateDoc(customerDocRef, {
      fcmToken: token,
      fcmTokenUpdated: new Date(),
      'preferences.notifications.push': true
    });

    console.log('Customer FCM token saved successfully');
    return true;
  } catch (error) {
    console.error('Error saving customer FCM token:', error);
    return false;
  }
};

// Remove FCM token (for logout or opt-out)
export const removeFCMToken = async (collectionName, documentId) => {
  try {
    const docRef = doc(db, collectionName, documentId);
    await updateDoc(docRef, {
      fcmToken: null,
      fcmTokenUpdated: new Date()
    });

    console.log('FCM token removed successfully');
    return true;
  } catch (error) {
    console.error('Error removing FCM token:', error);
    return false;
  }
};

// Detect whether a URL points to a GIF (including Giphy / Tenor CDN links)
const isGifUrl = (url) => {
  if (!url) return false;
  const lower = url.toLowerCase();
  return lower.includes('.gif') || lower.includes('giphy.com') || lower.includes('tenor.com');
};

// Inject an animated in-app toast that renders GIFs with full animation.
// This is the ONLY way to show an animated GIF from a push notification —
// OS notification banners always show a static first frame regardless of file
// type; there is no way to override that at the FCM or browser level.
const showGifNotificationToast = (title, body, imageUrl) => {
  // Remove any existing toast to avoid stacking
  const old = document.getElementById('fcm-gif-toast');
  if (old) old.remove();

  const toast = document.createElement('div');
  toast.id = 'fcm-gif-toast';
  toast.style.cssText = [
    'position:fixed', 'top:20px', 'right:20px', 'width:320px',
    'background:#fff', 'border-radius:12px',
    'box-shadow:0 8px 32px rgba(0,0,0,0.22)',
    'z-index:2147483647', 'overflow:hidden',
    'font-family:Arial,sans-serif',
    'animation:_fcmSlideIn 0.35s cubic-bezier(.22,.61,.36,1) both'
  ].join(';');

  const safeTitle = title ? title.replace(/</g, '&lt;').replace(/>/g, '&gt;') : '';
  const safeBody  = body  ? body.replace(/</g, '&lt;').replace(/>/g, '&gt;')  : '';

  toast.innerHTML = `
    <style>
      @keyframes _fcmSlideIn {
        from { transform: translateX(360px); opacity: 0; }
        to   { transform: translateX(0);     opacity: 1; }
      }
    </style>
    <img src="${imageUrl}" alt=""
      style="width:100%;display:block;max-height:220px;object-fit:cover;" />
    <div style="padding:12px 36px 14px 14px;">
      ${safeTitle ? `<strong style="display:block;font-size:14px;color:#222;margin-bottom:4px;">${safeTitle}</strong>` : ''}
      ${safeBody  ? `<span style="font-size:13px;color:#555;line-height:1.45;">${safeBody}</span>` : ''}
    </div>
    <button id="fcm-gif-toast-close"
      style="position:absolute;top:8px;right:8px;background:rgba(0,0,0,0.45);
             border:none;color:#fff;border-radius:50%;width:26px;height:26px;
             cursor:pointer;font-size:16px;line-height:26px;text-align:center;
             padding:0;">
      &times;
    </button>`;

  document.body.appendChild(toast);

  document.getElementById('fcm-gif-toast-close')
    .addEventListener('click', () => toast.remove());

  // Auto-dismiss after 10 s
  setTimeout(() => { if (toast.parentNode) toast.remove(); }, 10000);
};

// Listen for foreground messages
export const onForegroundMessage = (callback) => {
  // Return no-op immediately if basic browser support is missing
  if (!checkBrowserSupport()) {
    return () => {};
  }

  let unsubscribe = () => {};

  // Set up listener asynchronously
  (async () => {
    try {
      const supported = await checkMessagingSupport();
      if (!supported) return;

      const messagingInstance = await getMessagingInstance();
      if (!messagingInstance) return;

      const { onMessage } = await import('firebase/messaging');

      unsubscribe = onMessage(messagingInstance, (payload) => {
        console.log('Foreground message received:', payload);

        // Resolve image URL from either notification field or data payload
        const imageUrl = payload.notification?.image || payload.data?.imageUrl || null;
        const title    = payload.notification?.title || 'New Notification';
        const body     = payload.notification?.body  || '';

        if (imageUrl && isGifUrl(imageUrl)) {
          // GIF detected — render an in-app animated toast.
          // We intentionally skip the SW path here: OS notification banners
          // always display a static first frame and cannot animate GIFs.
          showGifNotificationToast(title, body, imageUrl);
        } else if (payload.notification && Notification.permission === 'granted') {
          // Non-GIF image or no image — show via service worker as before so
          // the OS notification banner appears with the static image.
          const swController = navigator.serviceWorker.controller;
          if (swController) {
            swController.postMessage({
              type: 'SHOW_FOREGROUND_NOTIFICATION',
              title,
              body,
              icon: payload.notification.icon || '/favicon.ico',
              image: imageUrl,
              data: payload.data || {},
            });
          } else {
            navigator.serviceWorker.ready.then((registration) => {
              registration.showNotification(title, {
                body,
                icon: payload.notification.icon || '/favicon.ico',
                badge: '/favicon.ico',
                image: imageUrl || undefined,
                data: payload.data || {},
              });
            });
          }
        }

        if (callback) {
          callback(payload);
        }
      });
    } catch (error) {
      console.log('Error setting up foreground message listener:', error.message);
    }
  })();

  return () => unsubscribe();
};

// Check if notifications are supported (synchronous basic check)
export const isNotificationSupported = () => {
  return checkBrowserSupport();
};

// Async check for full Firebase messaging support
export const isMessagingSupported = async () => {
  return await checkMessagingSupport();
};

// Get current notification permission status
export const getNotificationPermission = () => {
  if (!('Notification' in window)) return 'not-supported';
  return Notification.permission;
};
